import { v4 } from 'uuid';
import { toIsoString } from '../../chat/iso-date';
import type {
  DraftCronTriggerParams,
  DraftWebhookTriggerParams,
} from '../types';
import { WorkflowPersistence } from '../types';
import { Workflow as DomainWorkflow } from '../workflow';
import { WorkflowGraph } from '../workflow-graph';
import { Edge } from './edge';
import type { ForEachChildNode, ForEachNode } from './nodes';
import { WorkflowNodeType } from '../types';
import type { WorkflowGraphNode } from './workflow-graph-node';

export type WorkflowConstructorParams<
  TKind extends WorkflowPersistence = WorkflowPersistence.Persisted
> = {
  readonly persistence: TKind;
  readonly id?: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  readonly status?: string;
  readonly nodes: readonly WorkflowGraphNode[];
  readonly edges?: readonly Edge[];
  readonly cronTrigger?: Omit<
    DraftCronTriggerParams,
    'triggerId' | 'bindingId'
  >;
  readonly webhookTrigger?: Omit<
    DraftWebhookTriggerParams,
    'triggerId' | 'bindingId'
  >;
};

export type WorkflowSaveInput<TKind extends WorkflowPersistence> = {
  readonly ownerId: string;
  readonly workflow: DomainWorkflow;
  readonly ephemeral: TKind extends WorkflowPersistence.Ephemeral
    ? true
    : false;
};

export class Workflow<
  TKind extends WorkflowPersistence = WorkflowPersistence.Persisted
> {
  readonly persistence: TKind;
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  readonly status: string;

  private readonly graphNodes: readonly WorkflowGraphNode[];
  private readonly graphEdges: readonly Edge[];
  private readonly cronTrigger?: WorkflowConstructorParams<TKind>['cronTrigger'];
  private readonly webhookTrigger?: WorkflowConstructorParams<TKind>['webhookTrigger'];
  private domainWorkflow?: DomainWorkflow;

  constructor(params: WorkflowConstructorParams<TKind>) {
    this.persistence = params.persistence;
    this.id = params.id ?? v4();
    this.ownerId = params.ownerId;
    this.ownerOrgId = params.ownerOrgId;
    this.name = params.name;
    this.status = params.status ?? 'active';
    this.cronTrigger = params.cronTrigger;
    this.webhookTrigger = params.webhookTrigger;

    const nodeSet = new Map<string, WorkflowGraphNode>();
    for (const node of params.nodes) {
      for (const collected of node.collectNodes()) {
        if (nodeSet.has(collected.id)) {
          throw new Error(`Duplicate workflow graph node id: ${collected.id}`);
        }
        nodeSet.set(collected.id, collected);
      }
    }
    this.graphNodes = [...nodeSet.values()];
    this.graphEdges = mergeWorkflowEdges({
      nodes: this.graphNodes,
      explicitEdges: params.edges ?? [],
    });

    if (this.cronTrigger !== undefined && this.webhookTrigger !== undefined) {
      throw new Error(
        'Workflow cannot define both cronTrigger and webhookTrigger'
      );
    }
  }

  get isEphemeral(): boolean {
    return this.persistence === WorkflowPersistence.Ephemeral;
  }

  /** Definition graph view for entry-node / run-input resolution. */
  graph(): WorkflowGraph {
    return WorkflowGraph.create({
      nodes: this.graphNodes.map((node) => ({
        id: node.id,
        inputShape: null,
      })),
      edges: this.graphEdges.map((edge) => ({
        fromNodeId: edge.from.id,
        toNodeId: edge.to.id,
      })),
    });
  }

  /** Definition node ids with no incoming edges (manual run entrypoints). */
  entryNodeIds(): string[] {
    return this.graph().entryNodeIds();
  }

  toDomainWorkflow(): DomainWorkflow {
    if (this.domainWorkflow !== undefined) {
      return this.domainWorkflow;
    }
    this.domainWorkflow = DomainWorkflow.fromGraphDefinition({
      id: this.id,
      ownerId: this.ownerId,
      ownerOrgId: this.ownerOrgId,
      name: this.name,
      status: this.status,
      serializedNodes: this.graphNodes.map((node) =>
        node.toSerializedNode({
          workflowId: this.id,
          ownerId: this.ownerId,
          ownerOrgId: this.ownerOrgId,
          createdAt: toIsoString(new Date()),
          updatedAt: toIsoString(new Date()),
        })
      ),
      serializedEdges: this.graphEdges.map((edge) =>
        edge.toSerializedEdge({
          workflowId: this.id,
          ownerId: this.ownerId,
          ownerOrgId: this.ownerOrgId,
          createdAt: toIsoString(new Date()),
          updatedAt: toIsoString(new Date()),
        })
      ),
      cronTrigger: this.cronTrigger,
      webhookTrigger: this.webhookTrigger,
    });
    return this.domainWorkflow;
  }

  toSaveInput(saveOwnerId: string): WorkflowSaveInput<TKind> {
    return {
      ownerId: saveOwnerId,
      workflow: this.toDomainWorkflow(),
      ephemeral: this.isEphemeral,
    } as WorkflowSaveInput<TKind>;
  }
}

const mergeWorkflowEdges = (params: {
  nodes: readonly WorkflowGraphNode[];
  explicitEdges: readonly Edge[];
}): Edge[] => {
  const nodeIds = new Set(params.nodes.map((node) => node.id));
  const edgeByKey = new Map<string, Edge>();

  for (const node of params.nodes) {
    const inputFromNodeId = node.getInputFromNodeId();
    if (inputFromNodeId !== undefined && !nodeIds.has(inputFromNodeId)) {
      throw new Error(
        `Workflow node ${node.id} inputFrom references unknown node ${inputFromNodeId}`
      );
    }
    if (isForEachWithChild(node)) {
      const child = node.child;
      const forEachEdge = new Edge({
        from: node,
        to: child,
        on: 'success',
      });
      edgeByKey.set(forEachEdge.dedupeKey(), forEachEdge);
    }
  }

  for (const edge of params.explicitEdges) {
    if (edge.from.id === edge.to.id) {
      throw new Error('Workflow edge cannot connect a node to itself');
    }
    if (!nodeIds.has(edge.from.id)) {
      throw new Error(
        `Workflow edge references unknown source node: ${edge.from.id}`
      );
    }
    if (!nodeIds.has(edge.to.id)) {
      throw new Error(
        `Workflow edge references unknown target node: ${edge.to.id}`
      );
    }
    edgeByKey.set(edge.dedupeKey(), edge);
  }

  return [...edgeByKey.values()];
};

const findNodeById = (
  nodes: readonly WorkflowGraphNode[],
  nodeId: string
): WorkflowGraphNode => {
  const node = nodes.find((candidate) => candidate.id === nodeId);
  if (node === undefined) {
    throw new Error(`Workflow graph node not found: ${nodeId}`);
  }
  return node;
};

const isForEachWithChild = (
  node: WorkflowGraphNode
): node is ForEachNode & { child: ForEachChildNode } => {
  return (
    node.nodeType === WorkflowNodeType.ForEach &&
    'child' in node &&
    (node as ForEachNode).child !== undefined
  );
};
